import { Bind, registerComponent } from "@eusoft/webapp-core";
import { localTable, MessageBoxButton, useNetwork, userInteraction } from "@eusoft/webapp-framework";
import { handleError } from "@eusoft/webapp-framework/helpers/Error";
import { forModel } from "@eusoft/webapp-jsx";
import { Action, Content, contentInfo, formatCurrency, formatDate, formatString, formatText, InputField, MaterialIcon, NumberEditor, SingleSelector, SingleSelectorTemplates, staticItemsSourceWithIcon } from "@eusoft/webapp-ui";
import { paymentService } from "../../abstraction/IPaymentService";
import { OrderItemType, OrderType, PaymentMethod, SalePointType, type IExternalPayProvider } from "../../entities/Commands";
import { Authorize } from "../../services/Authorize";
import { context } from "../../services/Context";
import { executeAsync, type IExecuteOptions } from "../../services/DataService";
import { withSignManager } from "../../services/SignManager";
import { WithContext } from "../../services/WithContext";
import "./RechargeCardPage.scss";
import { isNativeApp } from "../../services/NativePayment";
import { printAsync } from "../../actions/Common";
import { formatMarkDown, formatPaymentMethod } from "../../helpers/Format";
import { bankPos, type IBankPosPayResultExtra } from "../../services/BankPosManager";

export interface IRechargeCardArgs {
    cardCode?: string;
}
interface IState {
    printReceipt: boolean;
}


export class RechargeCardPage extends Content<IRechargeCardArgs> {

    protected _state: IState;
    protected _lastPosPay: string | IBankPosPayResultExtra;

    constructor() {

        super();

        const payMethods = staticItemsSourceWithIcon(
            ["cash", PaymentMethod.Cash, <MaterialIcon name="money" style="green" />],
            ["bank-card", PaymentMethod.BankCard, <MaterialIcon name="credit_card" style="blue" />],
            ["gift", PaymentMethod.Gift, <MaterialIcon name="card_giftcard" style="purple" />]
        );

        this.init(RechargeCardPage, {

            title: "recharge-card",

            style: ["panel"],

            body: forModel(this, m => <>

                <InputField name="amount" value={Bind.twoWays(m.amount)} label="amount">
                    <NumberEditor />
                </InputField>

                <ul className="value-list">
                    {[5, 10, 15, 20, 50].forEach(a => <li on-click={() => m.amount = a}>{a}</li>)}
                </ul>

                <SingleSelector
                    template={SingleSelectorTemplates.Buttons}
                    name="pay-method"
                    style={["horizontal", "big"]}
                    itemsSource={payMethods}
                    value={Bind.twoWays(m.payMethod)} /> 

                <Action enabled={m.canBuy} type="global" onExecuteAsync={() => m.chargeCardAsync()}>
                    charge
                </Action>
            </>),
        });

        this.loadPageState();
    }

    protected loadPageState() {

        const json = localStorage.getItem(this.name);

        this._state = json ? JSON.parse(json) : {} as IState;

        if (this._state.printReceipt !== undefined)
            this.printReceipt = this._state.printReceipt;
    }

    protected savePageState(state: Partial<IState>) {

        if (state)
            Object.assign(this._state, state);

        localStorage.setItem(this.name, JSON.stringify(this._state));
    }

    override async onLoadAsync() {

        await bankPos.initSalePointAsync(context.salePoint?.id);
        return true;
    }

    async chargeCardAsync() {

        if (this.amount <= 0)
            return;

        this.savePageState({ printReceipt: this.printReceipt });

        const execOptions: IExecuteOptions = {
            errorMode: "full"
        }

        const orderId = await executeAsync("CreateOrder", {
            type: OrderType.Charge,
            merchantId: context.merchant.id,
            operatorId: context.operator?.id,
            salePointId: context.salePoint?.id,
            items: [{
                quantity: 1,
                unitAmount: this.amount,
                description: formatText("recharge") as string,
                productId: undefined,
                formatId: undefined,
                type: OrderItemType.Amount,
                vatPercentage: 0
            }]
        }, execOptions);

        if (!orderId)
            return;

        let external: IExternalPayProvider;

        if (this.payMethod == PaymentMethod.BankCard) {

            while (true) {

                if (this._lastPosPay &&
                    typeof this._lastPosPay == "object" &&
                    this._lastPosPay.isSuccess &&
                    this._lastPosPay.amount == this.amount) {

                    //Pick last valid payment
                }
                else {

                    this._lastPosPay = await bankPos.tryPayPos(context.salePoint?.id, {
                        amount: this.amount,
                        description: formatString("pay-app-charge"),
                        currency: "EUR",
                        orderId
                    });
                }


                let errorMessage: string;

                if (this._lastPosPay == "pos-error")
                    errorMessage = formatString("err-generic-bank-pos");

                if (typeof this._lastPosPay == "object") {

                    if (!this._lastPosPay.isSuccess)
                        errorMessage = this._lastPosPay.message ?? formatString("err-generic-bank-pos");
                }

                if (!errorMessage)
                    break;

                const res = await userInteraction.messageBoxAsync(<div className="text-icon">
                    <MaterialIcon name="error" style="red" />
                    {errorMessage}
                </div>, "error", MessageBoxButton.RetryCancel | MessageBoxButton.Ignore);

                if (res == MessageBoxButton.Ignore)
                    break;

                if (res == MessageBoxButton.Cancel)
                    return;
            }           
        }
        else
            this._lastPosPay = undefined;

        const auth = await executeAsync("CreatePayment", {
            orderId,
            salePointId: context.salePoint.id,
            operatorId: context.operator?.id,
        }, execOptions);

        if (!auth)
            return;

        const card = await handleError(() => paymentService.readCardAsync({
            payToken: auth,
            message: formatText("msg-wait-for-card-recharge") as string,
            amount: this.amount
        }));

        if (!card)
            return;

        if (!card?.payAuth) {
            await userInteraction.fullMessageAsync(formatText("err-pay-token"), "error", [MessageBoxButton.Close]);
            return;
        }

        if (typeof this._lastPosPay == "object") {

            external = {
                transactionId: this._lastPosPay.transactionCode,
                name: this._lastPosPay.providerName,
                //extra: this._lastPosPay.extra
            }
        }


        const payRes = await useNetwork(() =>
            withSignManager(cid =>
                executeAsync("PayOrder", {
                    connectionId: cid,
                    cardToken: card.payAuth,
                    orderId: orderId,
                    method: this.payMethod,
                    salePointId: context.salePoint.id,
                    operatorId: context.operator?.id,
                    external
                }, execOptions)));


        if (payRes.errorMessage)
            await userInteraction.fullMessageAsync(formatText(payRes.errorMessage), "error", [MessageBoxButton.Close]);
        else {

            if (this.printReceipt && isNativeApp()) {

                await printAsync("ChargeCart", {
                    cardCode: card.code,
                    amount: this.amount,
                    paymentMethod: formatPaymentMethod(this.payMethod),
                    time: new Date().toLocaleDateString(localTable.language, {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                        hour: "numeric",
                        minute: "2-digit",
                        second: "2-digit"
                    }),
                    salePointName: context.salePoint?.name
                })
            }

            await userInteraction.fullMessageAsync(
                formatMarkDown("msg-recharge-success-balance", formatCurrency(payRes.cardBalance)),
                "success",
                [{
                    name: "charge-new",
                    text: "charge-new-card",
                    executeAsync: async () => {
                        this.amount = 0;
                        this.payMethod = undefined;
                        this._lastPosPay = undefined;
                    }
                }]);


            this._lastPosPay = undefined;
        }

    }

    get canBuy() {
        return this.amount > 0 && this.payMethod != undefined;
    }


    amount: number;

    payMethod: PaymentMethod;

    printReceipt: boolean

    static override info = contentInfo({
        name: "recharge-card",
        route: "/cards/recharge",
        factory: () => new RechargeCardPage(),
        icon: <MaterialIcon style="yellow" name="arrow_circle_up" />,
        features: [
            Authorize(a => a.businessAccount()
                .resource("PaymentCard", "write")),
            WithContext("sale-point", SalePointType.Charge)
        ],
    });
}

registerComponent(RechargeCardPage, "RechargeCardPage");